/**
 * @fileoverview Registration access code gate page.
 * Requires users to enter a valid access code before they can reach the signup form.
 * Validates the code against the backend and stores a registration token in sessionStorage.
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Loading from '../components/Loading';

/**
 * Registration access code gate component.
 * On mount, checks if registration gating is enabled via the backend.
 * If not required, redirects directly to /signup.
 * Otherwise, renders a single-field form for the access code.
 * On successful validation, stores the registration token in sessionStorage
 * and navigates to /signup.
 *
 * @returns {JSX.Element} The register access page, a redirect, or a loading screen
 */
function RegisterAccess() {
  const { authenticated } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);

  /**
   * On mount, check whether the backend requires an access code for registration.
   * If not required, redirect straight to /signup.
   */
  useEffect(() => {
    let cancelled = false;

    /**
     * Fetch registration status from the backend.
     * Sets checking to false once complete, and navigates to /signup if no code is required.
     */
    async function checkStatus() {
      try {
        const res = await api.getRegistrationStatus();
        if (!cancelled && !res.data.requires_code) {
          navigate('/signup', { replace: true });
        }
      } catch {
        // If the check fails, still show the form as a safe default
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    checkStatus();
    return () => { cancelled = true; };
  }, [navigate]);

  // Authenticated users don't need this page
  if (authenticated) {
    return <Navigate to="/" replace />;
  }

  /**
   * Handle access code form submission.
   * Validates the code against the backend. On success, stores the registration
   * token in sessionStorage and navigates to /signup.
   *
   * @param {React.FormEvent} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError('Access code is required');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.validateRegistrationCode({ code: code.trim() });
      sessionStorage.setItem('registration_token', res.data.registration_token);
      navigate('/signup');
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Invalid access code');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading spinner while checking registration status
  if (checking) {
    return (
      <div className="register-access-page">
        <div className="register-access-card">
          <Loading variant="spinner" size="sm" message="Loading..." />
        </div>
      </div>
    );
  }

  return (
    <div className="register-access-page">
      <div className="register-access-card">
        <div className="register-access-header">
          <a href="https://cogslog.com" aria-label="COGSLOG home">
            <img
              src="/logos/stacked/cogslog-stacked-tagline-color.svg"
              alt="COGSLOG"
              className="auth-logo"
            />
          </a>
          <h1 className="register-access-title">Access Code Required</h1>
          <p className="register-access-subtitle">
            Enter your access code to create an account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="register-access-form" noValidate>
          {error && (
            <div id="register-access-error" className="err-msg" role="alert">
              {error}
            </div>
          )}

          <div className="field">
            <label htmlFor="access-code" className="label">Access Code</label>
            <input
              id="access-code"
              name="access-code"
              type="text"
              className="input"
              value={code}
              onChange={(e) => { setCode(e.target.value); if (error) setError(''); }}
              disabled={submitting}
              autoFocus
              autoComplete="off"
              aria-invalid={!!error}
              aria-describedby={error ? 'register-access-error' : undefined}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary register-access-submit"
            disabled={submitting}
          >
            {submitting ? 'Validating...' : 'Continue'}
          </button>
        </form>

        <p className="register-access-signin-link">
          Already have an account?{' '}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/login')}
          >
            Sign In
          </button>
        </p>
      </div>
    </div>
  );
}

export default RegisterAccess;
